import {
  setExportedIssuedInvoice,
  setExportedIssuedInvoices,
  setInvoicesUpdatedAt,
  seTIssuedInvoice,
  setModalInvoices,
  setModalPaymentSlips,
  setPaymentSlipsUpdatedAt,
  TExportIssuedInvoiceData,
  TIssuedInvoiceData,
} from "./reducers/globalState";

import { createAsyncThunk } from "@reduxjs/toolkit";
import apiService, { TApiService } from "../services/api";
import { TUserData } from "./reducers/userData";
import { TAccount, TAccountData } from "./reducers/account";
import {
  TGlobalState,
  TInvoiceData,
  TInvoiceEntryStatus,
  TPaymentSlipData,
  setInvoices,
  setPaymentSlips,
  setSelectedAccountState,
} from "./reducers/globalState";
import {
  setSelectedOrganization,
  setSettingsUserOrganizations,
  TOrganization,
  TOrganizationData,
} from "./reducers/organizations";
import { cnpjMask } from "../utils/formatters";
import {
  setMemberships,
  TMembershipData,
  TMembershipRole,
  TMembershipStatus,
} from "./reducers/memberships";
import { CancelToken } from "axios";
import {
  getQuerystringParams,
  handleGetDataFromLocalStorage,
  handleSetDataToLocalStorage,
  removeSpecialCharacters,
  setQuerystringUtils,
} from "../utils/miscellaneous";
import { localStorageConstants } from "../utils/constants";
import {
  setAccessKeyErrorsState,
  setQueueNFEState,
} from "./reducers/accessKeyImport";
import { issuedInvoicesMock } from "../pages/IssuedInvoices/mock";
import {
  exportIssuedInvoiceMock,
  TIssuedInvoiceExport,
} from "../pages/IssuedInvoices/Export/Details/mock";
import { exportIssuedInvoicesMock } from "../pages/IssuedInvoices/Export/mock";

export const fetchUserData = createAsyncThunk<
  TUserData,
  { cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/fetchUserData", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.getUserData(params.cancelToken);
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const fetchAccounts = createAsyncThunk<
  TAccountData,
  { cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/fetchAccounts",
  async (params, { extra, dispatch, getState, rejectWithValue }) => {
    const { apiService } = extra;
    const { globalState } = getState() as {
      globalState: { data: TGlobalState };
    };
    try {
      const account = localStorage.getItem("selectedAccount");
      if (account) {
        const parsedAccount = JSON.parse(account) as TAccount;
        dispatch(
          setSelectedAccountState({
            selectedAccount: parsedAccount,
            lastAccessedAccounts: globalState.data.lastAccessedAccounts,
          })
        );
      }
      const { search, page, items_per_page } = getQuerystringParams();

      const response = await apiService.getAccounts(
        params.cancelToken,
        search,
        page,
        items_per_page
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const fetchAccount = createAsyncThunk<
  TAccount,
  { accountUuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/fetchAccount", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.getAccount(
      params.accountUuid,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const synchronizeAccount = createAsyncThunk<
  any,
  { accountUuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/synchronizeAccount", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.synchronizeAccount(
      params.accountUuid,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const fetchMemberships = createAsyncThunk<
  TMembershipData,
  { cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/fetchMemberships", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;

  const queryParams = getQuerystringParams();

  try {
    const response = await apiService.getMemberships(
      queryParams.account_id,
      params.cancelToken,
      queryParams.page,
      queryParams.role as TMembershipRole[],
      queryParams.status as TMembershipStatus[]
    );
    return (response as any)?.data;
  } catch (_e) {
    try {
      if (queryParams.page) {
        queryParams.page = 1;
      }
      const response = await apiService.getMemberships(
        queryParams.account_id,
        params.cancelToken,
        queryParams.page,
        queryParams.role as TMembershipRole[],
        queryParams.status as TMembershipStatus[]
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
});

export const fetchOrganizations = createAsyncThunk<
  TOrganizationData,
  { cancelToken: CancelToken; search?: string },
  { extra: { apiService: TApiService } }
>(
  "data/fetchOrganizations",
  async (params, { extra, dispatch, rejectWithValue }) => {
    const { apiService } = extra;

    const queryParams = getQuerystringParams();

    const lastSelectedOrganizations =
      (handleGetDataFromLocalStorage(
        localStorageConstants.SELECT_ORGANIZATION
      ) as TOrganization) || null;

    try {
      const response = await apiService.getOrganizations(
        queryParams.account_id,
        params.cancelToken,
        undefined,
        params.search
      );
      let responseResults = (response?.data?.results as TOrganization[]) ?? [];
      // Mask CNPJ for all organizations
      responseResults = responseResults.map((organization) => {
        organization.cnpj = cnpjMask(organization.cnpj);
        return organization;
      });

      // Add lastSelectedOrganizations if not already present (by uuid)
      let mergedResults: TOrganization[] = responseResults;
      if (lastSelectedOrganizations && lastSelectedOrganizations.uuid) {
        const alreadyPresent = responseResults.some(
          (org) => org.uuid === lastSelectedOrganizations.uuid
        );
        if (!alreadyPresent) {
          mergedResults = [lastSelectedOrganizations, ...responseResults];
        }
      }
      responseResults = mergedResults;

      // Guarantee organization_id is set before calling fetchOrganizationPermissions
      if (
        queryParams.organization_id === undefined ||
        queryParams.organization_id.length === 0
      ) {
        queryParams.organization_id = responseResults[0]?.uuid;
        setQuerystringUtils(queryParams);
      }

      let selectedOrganization = responseResults.find(
        (organization) => organization.uuid === queryParams.organization_id
      );
      if (selectedOrganization) {
        handleSetDataToLocalStorage(
          localStorageConstants.SELECT_ORGANIZATION,
          selectedOrganization
        );
      }

      let lastAccessedOrgsRaw = localStorage.getItem(
        "lastAccessedOrganizations"
      );
      let organization: TOrganization | undefined;
      if (selectedOrganization) {
        if (
          selectedOrganization.account.uuid !== queryParams.account_id &&
          lastAccessedOrgsRaw
        ) {
          const lastAccessedOrgs = JSON.parse(
            lastAccessedOrgsRaw
          ) as TOrganization[];
          const sameAccountOrgs = lastAccessedOrgs.filter(
            (orgs) => orgs.account.uuid === queryParams.account_id
          );
          organization = sameAccountOrgs[0] ?? responseResults[0];
        } else {
          organization = selectedOrganization;
        }
      } else {
        organization = responseResults[0];
      }

      selectedOrganization = organization;

      // Guarantee organization_id is set in queryParams before permissions fetch
      if (selectedOrganization && selectedOrganization.uuid) {
        const newQueryParams = {
          ...queryParams,
          organization_id: selectedOrganization.uuid,
        };
        setQuerystringUtils(newQueryParams);
      }

      dispatch(setSelectedOrganization(selectedOrganization));

      dispatch(
        fetchMemberships({
          cancelToken: params.cancelToken,
        })
      );

      // Only call fetchOrganizationPermissions if organization_id is set
      let permissions = null;
      if (
        typeof queryParams.organization_id === "string" &&
        queryParams.organization_id.trim() !== "" &&
        queryParams.organization_id !== undefined &&
        queryParams.organization_id !== null
      ) {
        permissions = await dispatch(
          fetchOrganizationPermissions({ cancelToken: params.cancelToken })
        ).unwrap();
      }

      if (selectedOrganization) {
        const selectedOrganizationWithPermissions = {
          ...selectedOrganization,
          permissions,
        };
        dispatch(setSelectedOrganization(selectedOrganizationWithPermissions));
      }
      if (response) {
        response.data.results = responseResults;
      }
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const fetchOrganizationPermissions = createAsyncThunk<
  any,
  { cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/fetchOrganizationPermissions",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    const querystrings = getQuerystringParams();

    try {
      const response = await apiService.getOrganizationPermissions(
        querystrings.organization_id,
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const fetchInvoices = createAsyncThunk<
  TInvoiceData,
  {
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>(
  "data/fetchInvoices",
  async (params, { extra, dispatch, rejectWithValue }) => {
    const { apiService } = extra;

    const queryParams = getQuerystringParams();
    const p = {
      organizationUuid: queryParams.organization_id,
      date_issue_after: queryParams.end_date,
      date_issue_before: queryParams.start_date,
      has_accounts_payable: queryParams.has_payable_accounts,
      has_goods_receipt: queryParams.has_goods_receipt,
      manifestation_status: queryParams.manifestation_status,
      launch_status: queryParams.launch_status,
      page: queryParams.page,
      per_page: queryParams.items_per_page,
      search: queryParams.invoices_search,
    };

    if (p && p.search && p.search.length > 0) {
      p.page = 1;
      p.per_page = 100;
    }

    if (queryParams.organization_id) {
      try {
        let response;
        try {
          response = await apiService.getInvoices(p, params.cancelToken);
        } catch {
          response = undefined;
        }
        const invoicesData =
          response && response.data
            ? (response.data as TInvoiceData)
            : { count: 0, results: [], pages: [], current_page: 1 };
        dispatch(
          setInvoices({
            invoices: invoicesData,
            entryStatus:
              (queryParams.launch_status as TInvoiceEntryStatus) || undefined,
          })
        );
        // Fetch and dispatch invoice updatedAt
        let updatedAtResponse;
        try {
          updatedAtResponse = await apiService.getInvoiceUpdatedAt(
            queryParams.organization_id,
            params.cancelToken
          );
        } catch {
          updatedAtResponse = undefined;
        }
        if (updatedAtResponse && updatedAtResponse.data) {
          dispatch(
            setInvoicesUpdatedAt({
              responseStatus: updatedAtResponse.status,
              date: updatedAtResponse.data.updated_at,
            })
          );
        }
        return invoicesData;
      } catch (e) {
        return rejectWithValue(e);
      }
    }
    return { count: 0, results: [], pages: [], current_page: 1 };
  }
);

export const fetchIssuedInvoices = createAsyncThunk<
  TIssuedInvoiceData,
  {
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>(
  "data/fetchIssuedInvoices",
  async (params, { extra, dispatch, rejectWithValue }) => {
    const { apiService } = extra;

    const queryParams = getQuerystringParams();
    const p = {
      organizationUuid: queryParams.organization_id,
      date_issue_after: queryParams.end_date,
      date_issue_before: queryParams.start_date,
      has_accounts_payable: queryParams.has_payable_accounts,
      has_goods_receipt: queryParams.has_goods_receipt,
      manifestation_status: queryParams.manifestation_status,
      launch_status: queryParams.launch_status,
      page: queryParams.page,
      per_page: queryParams.items_per_page,
      search: queryParams.invoices_search,
    };

    if (p && p.search && p.search.length > 0) {
      p.page = 1;
      p.per_page = 100;
    }

    if (queryParams.organization_id) {
      try {
        let response;
        try {
          response = await apiService.getInvoices(p, params.cancelToken);
        } catch {
          response = undefined;
        }
        const invoicesData =
          response && response.data
            ? issuedInvoicesMock
            : { count: 0, results: [], pages: [], current_page: 1 };
        dispatch(
          seTIssuedInvoice({
            invoices: invoicesData,
            entryStatus:
              (queryParams.launch_status as TInvoiceEntryStatus) || undefined,
          })
        );
        // Fetch and dispatch invoice updatedAt
        let updatedAtResponse;
        try {
          updatedAtResponse = await apiService.getInvoiceUpdatedAt(
            queryParams.organization_id,
            params.cancelToken
          );
        } catch {
          updatedAtResponse = undefined;
        }
        if (updatedAtResponse && updatedAtResponse.data) {
          dispatch(
            setInvoicesUpdatedAt({
              responseStatus: updatedAtResponse.status,
              date: updatedAtResponse.data.updated_at,
            })
          );
        }
        return invoicesData;
      } catch (e) {
        return rejectWithValue(e);
      }
    }
    return { count: 0, results: [], pages: [], current_page: 1 };
  }
);

export const fetchExportedIssuedInvoices = createAsyncThunk<
  TExportIssuedInvoiceData,
  {
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>(
  "data/fetchExporTIssuedInvoice",
  async (params, { extra, dispatch, rejectWithValue }) => {
    const { apiService } = extra;

    const queryParams = getQuerystringParams();
    const p = {
      organizationUuid: queryParams.organization_id,
      date_issue_after: queryParams.end_date,
      date_issue_before: queryParams.start_date,
      has_accounts_payable: queryParams.has_payable_accounts,
      has_goods_receipt: queryParams.has_goods_receipt,
      manifestation_status: queryParams.manifestation_status,
      launch_status: queryParams.launch_status,
      page: queryParams.page,
      per_page: queryParams.items_per_page,
      search: queryParams.invoices_search,
    };

    if (p && p.search && p.search.length > 0) {
      p.page = 1;
      p.per_page = 100;
    }

    if (queryParams.organization_id) {
      try {
        let response;
        try {
          response = await apiService.getInvoices(p, params.cancelToken);
        } catch {
          response = undefined;
        }
        const invoicesData =
          response && response.data
            ? exportIssuedInvoicesMock
            : { count: 0, results: [], pages: [], current_page: 1 };
        dispatch(
          setExportedIssuedInvoices({
            invoices: invoicesData,
            entryStatus:
              (queryParams.launch_status as TInvoiceEntryStatus) || undefined,
          })
        );
        // Fetch and dispatch invoice updatedAt
        let updatedAtResponse;
        try {
          updatedAtResponse = await apiService.getInvoiceUpdatedAt(
            queryParams.organization_id,
            params.cancelToken
          );
        } catch {
          updatedAtResponse = undefined;
        }
        if (updatedAtResponse && updatedAtResponse.data) {
          dispatch(
            setInvoicesUpdatedAt({
              responseStatus: updatedAtResponse.status,
              date: updatedAtResponse.data.updated_at,
            })
          );
        }
        return invoicesData;
      } catch (e) {
        return rejectWithValue(e);
      }
    }
    return { count: 0, results: [], pages: [], current_page: 1 };
  }
);

export const fetchExportedIssuedInvoice = createAsyncThunk<
  TIssuedInvoiceExport | null,
  {
    exportId: string;
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>(
  "data/fetchIssuedExportedInvoice",
  async (params, { extra, dispatch, rejectWithValue }) => {
    const { apiService } = extra;

    const queryParams = getQuerystringParams();
    const p = {
      organizationUuid: queryParams.organization_id,
      date_issue_after: queryParams.end_date,
      date_issue_before: queryParams.start_date,
      has_accounts_payable: queryParams.has_payable_accounts,
      has_goods_receipt: queryParams.has_goods_receipt,
      manifestation_status: queryParams.manifestation_status,
      launch_status: queryParams.launch_status,
      page: queryParams.page,
      per_page: queryParams.items_per_page,
      search: queryParams.invoices_search,
    };

    if (p && p.search && p.search.length > 0) {
      p.page = 1;
      p.per_page = 100;
    }

    if (queryParams.organization_id) {
      try {
        let response;
        try {
          response = await apiService.getInvoices(p, params.cancelToken);
        } catch {
          response = undefined;
        }

        const invoice: TIssuedInvoiceExport =
          response && response.data
            ? exportIssuedInvoiceMock.results[parseInt(params.exportId, 10) - 1]
            : exportIssuedInvoiceMock.results[
                parseInt(params.exportId, 10) - 1
              ];
        dispatch(
          setExportedIssuedInvoice({
            invoice: invoice,
          })
        );

        return invoice;
      } catch (e) {
        return rejectWithValue(e);
      }
    }
    return null;
  }
);

export const fetchModalInvoices = createAsyncThunk<
  TInvoiceData,
  {
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>(
  "data/fetchModalInvoices",
  async (params, { extra, dispatch, rejectWithValue }) => {
    const { apiService } = extra;

    const queryParams = getQuerystringParams();
    const p = {
      organizationUuid: queryParams.organization_id,
      date_issue_after: queryParams.end_date,
      date_issue_before: queryParams.start_date,
      has_accounts_payable: queryParams.has_payable_accounts,
      has_goods_receipt: queryParams.has_goods_receipt,
      manifestation_status: queryParams.manifestation_status,
      launch_status: queryParams.launch_status,
      page: queryParams.page,
      per_page: queryParams.items_per_page,
      search: queryParams.link_invoices_search,
    };

    if (p && p.search && p.search.length > 0) {
      p.page = 1;
      p.per_page = 100;
    }

    if (queryParams.organization_id) {
      try {
        let response;
        try {
          response = await apiService.getInvoices(p, params.cancelToken);
        } catch {
          response = undefined;
        }
        const invoicesData =
          response && response.data
            ? (response.data as TInvoiceData)
            : { count: 0, results: [], pages: [], current_page: 1 };
        dispatch(
          setModalInvoices({
            invoices: invoicesData,
            entryStatus:
              (queryParams.launch_status as TInvoiceEntryStatus) || undefined,
          })
        );

        return invoicesData;
      } catch (e) {
        return rejectWithValue(e);
      }
    }
    return { count: 0, results: [], pages: [], current_page: 1 };
  }
);

export const fetchInvoice = createAsyncThunk<
  any,
  { invoiceUuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/fetchInvoice",
  async (params, { extra, dispatch, getState, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.getInvoice(
        params.invoiceUuid,
        params.cancelToken
      );
      const updatedInvoice = (response as any)?.data;
      const { globalState } = getState() as {
        globalState: { data: TGlobalState };
      };
      const invoicesData = globalState.data.invoiceState;
      const updatedResults = invoicesData.results.map((inv: any) =>
        inv.uuid === updatedInvoice.uuid ? updatedInvoice : inv
      );
      dispatch(
        setInvoices({ invoices: { ...invoicesData, results: updatedResults } })
      );
      return updatedInvoice;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const fetchPaymentSlips = createAsyncThunk<
  TPaymentSlipData,
  {
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>(
  "data/fetchPaymentSlips",
  async (params, { extra, dispatch, rejectWithValue }) => {
    const { apiService } = extra;

    const queryParams = getQuerystringParams();

    const p = {
      organizationUuid: queryParams.organization_id,
      due_date_after: queryParams.end_date,
      due_date_before: queryParams.start_date,
      has_accounts_payable: queryParams.has_payable_accounts,
      has_goods_receipt: queryParams.has_goods_receipt,
      launch_status: queryParams.launch_status,
      page: queryParams.page,
      per_page: queryParams.items_per_page,
      search: queryParams.payment_slips_search,
    };

    if (p && p.search && p.search.length > 0) {
      p.page = 1;
      p.per_page = 100;
    }

    if (queryParams.organization_id) {
      try {
        let response;
        try {
          response = await apiService.getPaymentSlips(p, params.cancelToken);
        } catch {
          response = undefined;
        }
        const paymentSlipsData =
          response && response.data
            ? (response.data as TPaymentSlipData)
            : { count: 0, results: [], pages: [], current_page: 1 };
        dispatch(
          setPaymentSlips({
            paymentSlips: paymentSlipsData,
            entryStatus:
              (queryParams.launch_status as TInvoiceEntryStatus) || undefined,
          })
        );

        let updatedAtResponse;
        try {
          updatedAtResponse = await apiService.getPaymentSlipUpdatedAt(
            queryParams.organization_id,
            params.cancelToken
          );
        } catch {
          updatedAtResponse = undefined;
        }
        if (updatedAtResponse && updatedAtResponse.data) {
          dispatch(
            setPaymentSlipsUpdatedAt({
              responseStatus: updatedAtResponse.status,
              date: updatedAtResponse.data.updated_at,
            })
          );
        }

        return paymentSlipsData;
      } catch (e) {
        return rejectWithValue(e);
      }
    }
    return { count: 0, results: [], pages: [], current_page: 1 };
  }
);

export const fetchModalPaymentSlips = createAsyncThunk<
  TPaymentSlipData,
  {
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>(
  "data/fetchModalPaymentSlips",
  async (params, { extra, dispatch, rejectWithValue }) => {
    const { apiService } = extra;

    const queryParams = getQuerystringParams();

    const p = {
      organizationUuid: queryParams.organization_id,
      due_date_after: queryParams.end_date,
      due_date_before: queryParams.start_date,
      has_accounts_payable: queryParams.has_payable_accounts,
      has_goods_receipt: queryParams.has_goods_receipt,
      page: queryParams.page,
      per_page: queryParams.items_per_page,
      search: queryParams.link_payment_slips_search,
    };

    if (p && p.search && p.search.length > 0) {
      p.page = 1;
      p.per_page = 100;
    }

    if (queryParams.organization_id) {
      try {
        let response;
        try {
          response = await apiService.getPaymentSlips(p, params.cancelToken);
        } catch {
          response = undefined;
        }
        const paymentSlipsData =
          response && response.data
            ? (response.data as TPaymentSlipData)
            : { count: 0, results: [], pages: [], current_page: 1 };
        dispatch(
          setModalPaymentSlips({
            paymentSlips: paymentSlipsData,
            entryStatus:
              (queryParams.launch_status as TInvoiceEntryStatus) || undefined,
          })
        );

        return paymentSlipsData;
      } catch (e) {
        return rejectWithValue(e);
      }
    }
    return { count: 0, results: [], pages: [], current_page: 1 };
  }
);

export const fetchPaymentSlip = createAsyncThunk<
  any,
  { paymentSlipUuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/fetchPaymentSlip",
  async (params, { extra, dispatch, getState, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.getPaymentSlip(
        params.paymentSlipUuid,
        params.cancelToken
      );
      const updatedPaymentSlip = (response as any)?.data;
      const { globalState } = getState() as {
        globalState: { data: TGlobalState };
      };
      const paymentSlipsData = globalState.data.paymentSlipState;
      const updatedResults = paymentSlipsData.results.map((inv: any) =>
        inv.uuid === updatedPaymentSlip.uuid ? updatedPaymentSlip : inv
      );
      dispatch(
        setPaymentSlips({
          paymentSlips: { ...paymentSlipsData, results: updatedResults },
        })
      );
      return updatedPaymentSlip;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const fetchCategories = createAsyncThunk<
  any,
  { cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/fetchCategories", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.getCategories(params.cancelToken);
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const fetchSuppliers = createAsyncThunk<
  any,
  { cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/fetchSuppliers", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.getSuppliers(params.cancelToken);
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const createInCashPayableThunk = createAsyncThunk<
  any,
  { payload: any; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/createInCashPayable", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.createInCashPayable(
      params.payload,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const createRecurringPayableThunk = createAsyncThunk<
  any,
  { payload: any; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/createRecurringPayable", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.createRecurringPayable(
      params.payload,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const createInstallmentsPayableThunk = createAsyncThunk<
  any,
  { payload: any; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/createInstallmentsPayable",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.createInstallmentsPayable(
        params.payload,
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const importPaymentSlipPDF = createAsyncThunk<
  any,
  { formData: FormData; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/importPaymentSlipPDF", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  const querystrings = getQuerystringParams();
  try {
    const response = await apiService.paymentSlipImportPDF(
      querystrings.organization_id,
      params.formData,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const linkPaymentSlipToPayable = createAsyncThunk<
  any,
  { paymentSlipUuid: string; payableId: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/linkPaymentSlipToPayable",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.setLinkPaymentSlipToPayable(
        params.paymentSlipUuid,
        params.payableId,
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const attachInvoiceToPaymentSlip = createAsyncThunk<
  any,
  { paymentSlipUuid: string; invoiceUuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/attachInvoiceToPaymentSlip",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.attachInvoiceToPaymentSlip(
        params.paymentSlipUuid,
        params.invoiceUuid,
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const updatePaymentSlipLaunchStatus = createAsyncThunk<
  any,
  {
    paymentSlipUuid: string;
    status: "decline" | "hang" | "finalize";
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>(
  "data/updatePaymentSlipLaunchStatus",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.updatePaymentSlipLaunchStatus(
        params.paymentSlipUuid,
        params.status,
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const renameInvoiceLabel = createAsyncThunk<
  any,
  { invoiceUuid: string; label: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/renameInvoiceLabel", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.renameInvoiceLabel(
      params.invoiceUuid,
      params.label,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const detachInvoiceFromPaymentSlip = createAsyncThunk<
  any,
  { paymentSlipUuid: string; invoiceUuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/detachInvoiceFromPaymentSlip",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.detachInvoiceFromPaymentSlip(
        params.paymentSlipUuid,
        params.invoiceUuid,
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);
export const importInvoiceFiles = createAsyncThunk<
  any,
  { formData: FormData; type: "danfe" | "xml"; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/importInvoiceFiles", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  const querystrings = getQuerystringParams();
  try {
    const response = await apiService.invoiceImportFiles(
      querystrings.organization_id,
      params.formData,
      params.type,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const linkInvoiceToPayable = createAsyncThunk<
  any,
  { invoiceUuid: string; payableId: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/linkInvoiceToPayable", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.setLinkInvoiceToPayable(
      params.invoiceUuid,
      params.payableId,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const createExpressGoodsStockEntry = createAsyncThunk<
  any,
  { invoiceUuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/createExpressGoodsStockEntry",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    const querystrings = getQuerystringParams();
    try {
      const response = await apiService.createExpressGoodsStockEntry(
        querystrings.organization_id,
        params.invoiceUuid,
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const attachPaymentSlipToInvoice = createAsyncThunk<
  any,
  { invoiceUuid: string; paymentSlipUuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/attachPaymentSlipToInvoice",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.attachPaymentSlipToInvoice(
        params.invoiceUuid,
        params.paymentSlipUuid,
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const detachPaymentSlipFromInvoice = createAsyncThunk<
  any,
  { invoiceUuid: string; paymentSlipUuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/detachPaymentSlipFromInvoice",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.detachPaymentSlipFromInvoice(
        params.invoiceUuid,
        params.paymentSlipUuid,
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const renamePaymentSlipLabel = createAsyncThunk<
  any,
  { paymentSlipUuid: string; label: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/renamePaymentSlipLabel", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.renamePaymentSlipLabel(
      params.paymentSlipUuid,
      params.label,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const deleteMember = createAsyncThunk<
  any,
  { memberUuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("data/deleteMember", async (params, { extra, dispatch, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.deleteMember(
      params.memberUuid,
      params.cancelToken
    );

    dispatch(setMemberships(response?.data as TMembershipData));

    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const setOrganizationsByMembershipThunk = createAsyncThunk<
  any,
  {
    membershipUuid: string;
    organizationsUuid: string[];
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>(
  "data/setOrganizationsByMembership",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.setOrganizationsByMembership(
        params.membershipUuid,
        params.organizationsUuid,
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const createMemberThunk = createAsyncThunk<
  any,
  {
    member: {
      account: string;
      email: string;
      role: TMembershipRole;
      is_active: boolean;
      organizations: string[];
    };
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>("data/createMember", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.createMember(
      params.member,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const editMemberThunk = createAsyncThunk<
  any,
  {
    uuid: string;
    member: {
      account: string;
      email: string;
      role: TMembershipRole;
      is_active: boolean;
      organizations: string[];
    };
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>("data/editMember", async (params, { extra, rejectWithValue }) => {
  const { apiService } = extra;
  try {
    const response = await apiService.editMember(
      params.uuid,
      params.member,
      params.cancelToken
    );
    return (response as any)?.data;
  } catch (e) {
    console.error(e);
    return rejectWithValue(e);
  }
});

export const toggleOrganizationVisibilityThunk = createAsyncThunk<
  any,
  { organizationId: string; isActive: boolean; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/toggleOrganizationVisibility",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    try {
      const response = await apiService.toggleOrganizationVisibility(
        params.organizationId,
        params.isActive,
        params.cancelToken
      );

      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const filterOrganizationByVisibilityThunk = createAsyncThunk<
  any,
  {
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService } }
>(
  "data/filterOrganizationByVisibility",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;
    const querystrings = getQuerystringParams();
    try {
      const visibilityBool =
        querystrings.visibility === "visible"
          ? true
          : querystrings.visibility === "hidden"
          ? false
          : undefined;

      const response = await apiService.filterOrganizationByVisibility(
        querystrings.account_id,
        visibilityBool,
        params.cancelToken,
        querystrings.orgs_search,
        querystrings.page
      );

      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

// Thunk to set selected organization and fetch its permissions asynchronously
export const setSelectedOrganizationThunk = createAsyncThunk<
  void,
  { selectedOrganization: TOrganization; cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>("organizations/setSelectedOrganization", async (params, { dispatch }) => {
  const { selectedOrganization, cancelToken } = params;
  // Guarantee selectedOrganization is initialized
  if (!selectedOrganization || !selectedOrganization.uuid) {
    // Do not proceed if not initialized
    console.warn(
      "setSelectedOrganizationThunk: selectedOrganization is not initialized"
    );
    return;
  }
  // Fetch permissions for the organization
  const permissions = await dispatch(
    fetchOrganizationPermissions({ cancelToken })
  ).unwrap();
  // Create a new object to avoid mutating state
  const orgWithPermissions = {
    ...selectedOrganization,
    permissions,
  };
  dispatch(setSelectedOrganization(orgWithPermissions));
});

// Thunk to fetch organizations for settings/users/:id route
export const fetchOrganizationsForUserSettings = createAsyncThunk<
  TOrganizationData,
  { cancelToken: CancelToken },
  { extra: { apiService: TApiService } }
>(
  "data/fetchOrganizationsForUserSettings",
  async (params, { extra, dispatch, rejectWithValue }) => {
    const { apiService } = extra;

    const queryParams = getQuerystringParams();

    try {
      const response = await apiService.getOrganizations(
        queryParams.account_id,
        params.cancelToken,
        queryParams.page,
        queryParams.users_search,
        queryParams.items_per_page
      );
      let responseResults = (response?.data?.results as TOrganization[]) ?? [];
      responseResults = responseResults.map((organization) => {
        organization.cnpj = cnpjMask(organization.cnpj);
        return organization;
      });

      dispatch(
        setSettingsUserOrganizations({
          ...(response?.data ?? {
            count: 0,
            results: [],
            pages: [],
            current_page: 1,
          }),
          results: responseResults,
        })
      );

      if (response) {
        response.data.results = responseResults;
      }
      return (response as any)?.data;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const downloadInvoicesCompressedFiles = createAsyncThunk<
  any,
  {
    option: "xml" | "darf";
    itemsUuid: string[];
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService }; dispatch: any }
>(
  "data/downloadInvoicesCompressedFiles",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;

    const querystrings = getQuerystringParams();

    try {
      const response = await apiService.getInvoicesCompressedFilesDownloadLink(
        {
          option: params.option,
          organizationUuid: querystrings.organization_id,
          itemsUuid: params.itemsUuid,
        },
        params.cancelToken
      );
      return (response as any)?.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

// Download compressed payment slip files thunk
export const downloadPaymentSlipsCompressedFiles = createAsyncThunk<
  any,
  {
    itemsUuid: string[];
    cancelToken: CancelToken;
  },
  { extra: { apiService: TApiService }; dispatch: any }
>(
  "data/downloadPaymentSlipsCompressedFiles",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;

    const querystrings = getQuerystringParams();

    try {
      const response =
        await apiService.getPaymentSlipsCompressedFilesDownloadLink(
          {
            organizationUuid: querystrings.organization_id,
            itemsUuid: params.itemsUuid,
          },
          params.cancelToken
        );
      return (response as any)?.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const onSubmitAccessKeys = createAsyncThunk<
  boolean[], // array of error flags
  { cancelToken: CancelToken },
  { extra: any }
>(
  "accessKeyImport/validateAccessKeys",
  async (params, { dispatch, getState, rejectWithValue }) => {
    const accessKeys = (getState() as any).accessKeyImport
      .accessKeys as string[];

    const errors = accessKeys.map((key: string) => key.length !== 44);

    if (errors.some(Boolean)) {
      dispatch(setAccessKeyErrorsState(errors));
      return rejectWithValue(errors);
    } else {
      dispatch(setAccessKeyErrorsState([]));
      dispatch(createAccessKeys({ cancelToken: params.cancelToken }));
    }

    return errors.filter(Boolean) as boolean[];
  }
);

export const createAccessKeys = createAsyncThunk<
  boolean[], // array of error flags
  { cancelToken: CancelToken },
  { extra: any }
>(
  "accessKeyImport/createAccessKeys",
  async (params, { getState, rejectWithValue }) => {
    const accessKeys = (getState() as any).accessKeyImport
      .accessKeys as string[];
    const selectedOrganization = (getState() as any).organizations
      .selectedOrganization as TOrganization;

    const results = await Promise.all(
      accessKeys.map(async (key: string) => {
        try {
          await apiService.postNFEQueue(
            {
              cnpj: removeSpecialCharacters(selectedOrganization.cnpj),
              access_key: key,
            },
            params.cancelToken
          );
          return null; // No error
        } catch (err) {
          return err; // Store error
        }
      })
    );

    if (results.filter(Boolean).length > 0) {
      return rejectWithValue(results.filter(Boolean));
    }

    return results.filter(Boolean) as boolean[];
  }
);

export const getQueueNFE = createAsyncThunk<
  any,
  { hasError?: boolean; cancelToken: CancelToken; mock?: number },
  { extra: { apiService: TApiService }; dispatch: any }
>(
  "accessKeyImport/getQueueNFE",
  async (params, { extra, getState, rejectWithValue, dispatch }) => {
    const { apiService } = extra;

    const selectedOrganization = (getState() as any).organizations
      .selectedOrganization;
    if (selectedOrganization.cnpj) {
      try {
        const response = /*  params.mock
          ? {
              data: {
                count: params.mock,
                next: null,
                previous: null,
                results: [
                  {
                    uuid: "2a07ee2e-59c6-4a1b-b040-66136eb89409",
                    cnpj: "44568625000120",
                    access_key: "35251072962327000155550010001848351120087840",
                    has_error: true,
                    reason:
                      "Rejeicao: NF-e inexistente para a chave de acesso informada",
                    created_at: "2025-10-27T14:29:35.232767Z",
                    updated_at: "2025-10-27T14:29:36.442566Z",
                  },
                  params.mock
                    ? {
                        uuid: "cdbdb996-1590-40c7-8963-ddc0eba5970c",
                        cnpj: "44568625000120",
                        access_key:
                          "35251143528980000102550510000161731613719653",
                        has_error: false,
                        reason: "",
                        created_at: "2025-11-08T14:30:22.434950Z",
                        updated_at: "2025-11-08T14:30:22.434933Z",
                      }
                    : undefined,
                ],
              },
            }
          : */ await apiService.getQueueNFE(
          {
            cnpj: removeSpecialCharacters(selectedOrganization.cnpj),
            hasError: params.hasError,
          },
          params.cancelToken
        );

        const errorsCount = (response?.data?.results).filter(
          (item: { has_error: boolean }) => item.has_error
        ).length;

        dispatch(
          setQueueNFEState({
            count: response.data.count ?? 0,
            next: response.data.next ?? null,
            previous: response.data.previous ?? null,
            results: response.data.results ?? [],
            errorsCount: errorsCount,
          })
        );

        return (response as any)?.data;
      } catch (e) {
        return rejectWithValue(e);
      }
    }
  }
);

export const deleteNFEQueue = createAsyncThunk<
  any,
  { uuid: string; cancelToken: CancelToken },
  { extra: { apiService: TApiService }; dispatch: any }
>(
  "accessKeyImport/deleteNFEQueue",
  async (params, { extra, rejectWithValue }) => {
    const { apiService } = extra;

    try {
      const response = await apiService.deleteNFEQueue(
        {
          uuid: params.uuid,
        },
        params.cancelToken
      );

      return (response as any)?.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);
